import { useContext, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import { setUser } from "redux/slices/userSlice";
import { getAgent } from "redux/agent/slices/getAgentSlice";
import { removePolicies } from "redux/slices/searchPoliciesSlice";
import { useBreadcrumb } from "hooks/useBreadcrumb";

import { getUser } from "service/userservice/getUser";
import { convertToTitleCase } from "js/formatting";
import { getImage, isCSR, isCSRAdmin, isFlagEnabled } from "js/utilities";
import { useTrackCSRLoginAsUser } from "logging";

import FormInputError from "components/General/FormInputError";
import EverlakeLoadingOverlay from "components/General/EverlakeLoadingOverlay";
import config from "config/config";
import { useTranslation } from "react-i18next";
import { getPolicies } from "service/agent/getPolicies";
import { AgentData } from "context/Agent/DataProvider";

export default function CsrSearchboard({ portal }) {
  const { setAgentData, setIsAgentPoliciesLoading } = useContext(AgentData);
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { csrUser } = useSelector((s) => s.csrUser);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();
  const [formInputError, setFormInputError] = useState();
  const [loading, setLoading] = useState();
  const trackCSRLoginAsUser = useTrackCSRLoginAsUser();

  async function onSubmit(data) {
    // call getUseProfile portal api for agent Id and alternative Id

    setLoading(true);
    let userProfile = await getUser(data.username, portal).catch((error) => {
      console.log(error);
    });
    setLoading(false);
    dispatch(setUser(userProfile));
    if (userProfile) {
      if (portal === "agent") {
        setFormInputError("");
        let policyIds = [];

        // get all polices for logged in agent
        for (let i = 0; i < userProfile?.agentHierarchy.length; i++) {
          if (userProfile?.agentHierarchy[i]?.policyIds)
            policyIds.push(...userProfile?.agentHierarchy[i].policyIds);
        }

        // clear oppesite portal polices
        dispatch(removePolicies());

        // this is for profile page setup
        dispatch(
          getAgent({ agentId: userProfile.agentViewingId, updateStore: true })
        );
        getPolicies(policyIds)
          .then((response) => {
            setAgentData({
              agentPolicies: response?.agentPolicies,
              filteredPolicies: response?.filteredPolicies,
              agentClaims: response?.agentClaims,
            });
            setIsAgentPoliciesLoading(false);
          })
          .catch((error) => {
            setIsAgentPoliciesLoading(false);
          });

        // fetch by agent hierarchy policy ID(s)
        setLoading(false);
        trackCSRLoginAsUser(userProfile);
        navigate("/dashboard");
      }
    } else {
      setLoading(false);
      setFormInputError(t("AgentCsrSearchboard-NoUser"));
    }
  }

  useBreadcrumb([]);

  return (
    <>
      {csrUser?.userId && (
        <a className="ui-skip-to-content" href="#mainContent">
          {t("AgentCsrSearchboard-SkipContent")}
        </a>
      )}

      <div id="id_csr">
        <div id="id_csrTopBar">
          <div
            id="id_utilityMenu"
            className="ui-utilitybar bg-[color:var(--theme-bgcolor-portal)]"
          >
            <div className="container pt-4">
              <div className="flex justify-between flex-row-reverse items-center lg:grid lg:gap-x-2 lg:grid-cols-[1fr_max-content_max-content] lg:items-center">
                <div className="lg:flex lg:items-center px-0">
                  <img
                    src={getImage("image10.svg")}
                    className="h-5 px-0"
                    alt="NTT Data Logo"
                  />
                </div>
                <div className="flex text-[color:var(--theme-color-portal-title)] font-semibold px-0 border-theme-rule">
                  <div className="lg:flex">
                    <div id="everlakeLogoHeader">
                      <img
                        src={getImage("logo_blue.png")}
                        className="ui-logo-header"
                        alt={`${convertToTitleCase(config.COMPANY_NAME)} Logo`}
                      />
                    </div>
                  </div>
                </div>
                <div className="flex text-[color:var(--theme-color-portal-title)] font-semibold px-0 border-theme-rule">
                  {t("AgentCsrSearchboard-AgentPortal")}
                </div>
              </div>
              <hr className="mt-4 mb-1" />

              {location.pathname !== "/csradmin" &&
                location.pathname !== "/csr/forms" && (
                  <>
                    <div className="flex justify-between">
                      <div className="flex gap-2">
                        {isFlagEnabled("ENABLE_CSR_ADMIN") &&
                        isCSRAdmin(csrUser) ? (
                          <Link
                            to="/csradmin"
                            className="ui-btn-primary underline"
                          >
                            {t("AgentCsrSearchboard-AdminPage")}
                          </Link>
                        ) : null}
                        {isCSR(csrUser) || isCSRAdmin(csrUser) ? (
                          <Link
                            to="/csr/forms"
                            className="ui-btn-primary underline"
                          >
                            Forms
                          </Link>
                        ) : null}
                      </div>
                      <button
                        className="ui-btn-hyperlink text-xs"
                        onClick={() => navigate("/")}
                      >
                        {t("AgentCsrSearchboard-LogoutCSR")}
                      </button>
                    </div>
                    <div className="ui-header-search-desktop container">
                      <div className="mx-auto w-3/5 xl:w-1/2">
                        <div className="mx-auto w-3/5 xl:w-1/2 py-4">
                          <p className="font-bold">
                            {t("AgentCsrSearchboard-AgentLookup")}
                          </p>
                        </div>
                        <EverlakeLoadingOverlay isLoading={loading}>
                          <form
                            className="flex flex-col sm:flex-row"
                            onSubmit={handleSubmit(onSubmit)}
                          >
                            <div className="flex flex-col flex-grow justify-start sm:mr-4">
                              <div className="relative">
                                <input
                                  id="ID_username"
                                  type="text"
                                  placeholder="Enter agent user name or agent-id"
                                  aria-placeholder="Enter agent user name or agent-id"
                                  aria-labelledby="ID_usernameLabel"
                                  aria-describedby="ID_usernameError"
                                  className="border border-ui-gray-medium h-9 w-full p-2 pl-8 rounded"
                                  {...register("username", {
                                    required: {
                                      value: true,
                                      message: t(
                                        "AgentCsrSearchboard-UserName"
                                      ),
                                    },
                                  })}
                                />
                                <svg
                                  className="ui-icon w-4 h-4 absolute text-theme-action left-0 top-1/2 mt-[-.5rem] ml-2"
                                  focusable="false"
                                  aria-hidden="true"
                                  role="presentation"
                                >
                                  <use href="#ui-icon-search"></use>
                                </svg>
                              </div>
                              <div id="ID_usernameError">
                                {errors.username && (
                                  <div className="ui-input-error">
                                    {errors.username.message}
                                  </div>
                                )}
                              </div>
                            </div>
                            <div>
                              <button className="ui-btn-primary h-9 w-32 mx-auto ui-btn-min-w mt-4 sm:mt-0">
                                {t("AgentCsrSearchboard-Submit")}
                              </button>
                            </div>
                          </form>
                        </EverlakeLoadingOverlay>
                        {!loading && (
                          <FormInputError errorMessage={formInputError} />
                        )}
                      </div>
                    </div>
                  </>
                )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
